// ── Staging-tenant predicate ─────────────────────────────────────────
//
// Single source of truth for "which tenants act as our staging ring."
// The Riverr team uses Bespoke Threads as its canonical pre-GA tenant
// — new frontend features land there first, get exercised against real
// production-shaped data, and graduate to the rest of the fleet once
// they bake.
//
// Membership of the staging ring also includes:
//   - localhost                          → developer machines
//   - riverr-enterprise-user.web.app     → Firebase preview URL
//   - bigfrog.myriverr.com               → Big Frog admin, opted in
//                                          while quotes / sales orders
//                                          bake on bespokethreads
//   - app.girltribeco.com                → Girl Tribe admins — production
//                                          shop that should see the full
//                                          admin rail (Customers, Register,
//                                          Tickets, Inserts, …) not the
//                                          staging-gated subset
//
// Franchise shop hosts (`bigfrog-*.myriverr.com`) are NOT in this ring.
// `isBigFrogStagingHost` is a separate check so Contacts can show on
// those shops without turning on the rest of the ring (quote pricing
// v3, Register, Tickets).
//
// (The first two are aliased through `isDevDomain` in `utils/index.js`.)
//
// To add a new staging tenant (e.g. a friendly customer that wants
// early access to a feature you're prototyping), OR their domain
// predicate in below. To remove one, drop the OR. Lifting a feature
// from staging to GA happens in `featureFlags.js`, not here — this
// file decides who is in the ring, not what they get.

import {
  isBespokeThreadsDomain,
  isBigfrogDomian,
  isDevDomain,
  isGirlTribeCoDomain,
} from "../utils";

/** Big Frog admin portal and `{shop}.myriverr.com` franchise hosts. */
export const isBigFrogStagingHost = (host = "") => {
  const h = String(host || "").toLowerCase();
  return (
    h === "bigfrog.myriverr.com" ||
    /^bigfrog-[a-z0-9-]+\.myriverr\.com$/.test(h)
  );
};

/**
 * `true` when the current page is being served on a tenant we treat as
 * staging. Computed once at module load (the underlying domain checks
 * are IIFE-evaluated booleans in `utils/index.js`).
 *
 * Exposed as a function rather than a const so a future enhancement
 * can layer runtime overrides on top (e.g. a `?riverr_staging=1` URL
 * query param for QA, or a logged-in admin's preference flag) without
 * having to change every call site.
 */
export const isStagingTenant = () =>
  isBespokeThreadsDomain ||
  isBigfrogDomian ||
  isGirlTribeCoDomain ||
  isDevDomain;
